// RiskStatus Configuration - UNIFIED APPROACH
// Manages Risk Assessment statuses (Draft, In Review, Approved, Rejected, etc.)
// Based on Prisma RiskAssessmentStatus model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const riskStatusConfig = {
  entityName: 'RiskStatus',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/risk-assessment-status',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // RiskStatus specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Risk Assessment statuses typically don't have lookup dependencies
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle
  },
  
  // Service configuration
  service: {
    name: 'riskStatusService',
    methods: {
      getAll: 'getAllRiskStatuses',
      getById: 'getRiskStatusById', 
      create: 'createRiskStatus',
      update: 'updateRiskStatus',
      delete: 'deleteRiskStatus',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Status Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter Risk Assessment status name (e.g., Draft, In Review, Approved, Rejected)',
      table: { header: 'Status Name', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'Status name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Status is active and available for use',
      description: 'Inactive statuses cannot be used for new Risk Assessment records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true,
        message: 'Display order must be a whole number between 1 and 999'
      },
      helperText: 'Controls the order in dropdown lists and displays',
      step: 1,
      decimals: 0,
      allowNegative: false
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color for visual categorization',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual categorization in lists and reports'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['record_order']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false],
    record_order: 'asc'
  },

  // Messages Configuration
  messages: {
    loading: 'Loading Risk Assessment statuses...',
    noData: 'No Risk Assessment statuses found. Click "Add Risk Status" to create the first status.',
    loadError: 'Failed to load Risk Assessment statuses',
    saveSuccess: 'Risk Assessment status saved successfully!',
    saveError: 'Failed to save Risk Assessment status',
    deleteSuccess: 'Risk Assessment status deleted successfully!',
    deleteError: 'Failed to delete Risk Assessment status',
    deleteConfirm: 'Are you sure you want to delete this Risk Assessment status? This action cannot be undone and may affect Risk Assessment records that reference this status.',
    activateSuccess: 'Risk Assessment status activated successfully!',
    deactivateSuccess: 'Risk Assessment status deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Risk Assessment Status Information',
        icon: '🎯',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'isActive' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('RiskStatus'),

  // Labels Configuration
  labels: {
    singular: 'Risk Assessment Status',
    plural: 'Risk Assessment Statuses',
    addButton: 'Add Risk Status',
    editButton: 'Edit Risk Status',
    deleteButton: 'Delete Risk Status',
    pageTitle: 'Risk Assessment Status Management',
    pageSubtitle: 'Manage Risk Assessment statuses for tracking risk evaluation progress',
    searchPlaceholder: 'Search Risk Assessment statuses by name...',
    basicInfoSection: 'Risk Assessment Status Information',
    activateRiskStatus: 'Activate Risk Status',
    deactivateRiskStatus: 'Deactivate Risk Status',
    reorderRiskStatuses: 'Reorder Risk Assessment Statuses'
  },

  // Fields to remove during data preparation for submission
  fieldsToRemove: [
    'id', 'createdAt', 'updatedAt', 'createdById', 'updatedById',
    'riskAssessmentStatus'
  ],

  // Export filename for data exports
  exportFileName: 'risk-assessment-statuses',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        delete transformed.record_order;
      } else {
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order;
        }
      }
    }
    
    return transformed;
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null
};
